// Master-only `/admin/users` list with action menu. Shows every portal
// account across every tenant scope (master_admin rows live at
// tenant_id=null). Operators kick off invite + role/status changes,
// session/TOTP resets, and tenant moves from here.
//
// P1-4 (Agent B): expand the read-only list with row actions:
//   - Invite user (modal)
//   - Edit name / role / tenant
//   - Suspend / reactivate
//   - Reset TOTP
//   - Invalidate sessions
//   - Resend invite
// Last-master-protection 422s are surfaced clearly. resident accounts
// are not creatable here — operators are routed to /residents.

import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Button, Icon, Spinner } from '@ng-unifi/ui';
import { apiClient, ApiError } from '../lib/api.js';
import { rowMenuStyle } from '../lib/rowMenuStyle.js';
import { useAuth } from '../auth/AuthContext.js';
import { roleLabel, statusLabel } from '../lib/humanLabels.js';

type Role = 'master_admin' | 'tenant_admin' | 'tenant_manager' | 'tenant_viewer' | 'resident';

const NON_RESIDENT_ROLES: Role[] = [
  'master_admin',
  'tenant_admin',
  'tenant_manager',
  'tenant_viewer',
];

interface AdminUserRow {
  id: string;
  email: string;
  name: string;
  role: Role;
  tenant_id: string | null;
  status: 'invited' | 'active' | 'suspended';
  mfa_enrolled: boolean;
  last_login_at: string | null;
  created_at: string;
}

interface TenantSummary {
  id: string;
  name: string;
  status: 'active' | 'suspended' | 'archived';
}

const ROLE_TONE: Record<Role, string> = {
  master_admin: 'bg-primary-container text-on-primary',
  tenant_admin: 'bg-secondary-container text-on-secondary-container',
  tenant_manager: 'bg-tertiary-fixed text-on-tertiary-fixed',
  tenant_viewer: 'bg-surface-container-high text-on-surface-variant',
  resident: 'bg-surface-container-low text-on-surface',
};

const STATUS_TONE: Record<AdminUserRow['status'], string> = {
  invited: 'bg-tertiary-fixed text-on-tertiary-fixed',
  active: 'bg-secondary-container text-on-secondary-container',
  suspended: 'bg-error-container text-on-error-container',
};

async function loadAdminUsers(): Promise<AdminUserRow[]> {
  const res = await apiClient.get<{ data: AdminUserRow[] }>('/api/v1/admin/users', {
    skipTenantScope: true,
  });
  return res.data;
}

async function loadTenants(): Promise<TenantSummary[]> {
  try {
    const res = await apiClient.get<{ data: TenantSummary[] }>('/api/v1/tenants', {
      skipTenantScope: true,
    });
    return res.data;
  } catch {
    return [];
  }
}

type ActionKind =
  | 'invite'
  | 'edit'
  | 'role'
  | 'tenant'
  | 'suspend'
  | 'delete'
  | 'reactivate'
  | 'reset-password'
  | 'reset-mfa'
  | 'invalidate'
  | 'resend-invite';

export function AdminUsersPage() {
  const { state } = useAuth();
  const [rows, setRows] = useState<AdminUserRow[] | null>(null);
  const [tenants, setTenants] = useState<TenantSummary[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [reloadTick, setReloadTick] = useState(0);
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [action, setAction] = useState<{
    kind: ActionKind;
    user: AdminUserRow | null;
  } | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const reload = useCallback(() => setReloadTick((t) => t + 1), []);

  useEffect(() => {
    let cancelled = false;
    setRows(null);
    setError(null);
    Promise.all([loadAdminUsers(), loadTenants()])
      .then(([users, ts]) => {
        if (cancelled) return;
        setRows(users);
        setTenants(ts);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof ApiError ? err.message : 'Failed to load users');
      });
    return () => {
      cancelled = true;
    };
  }, [reloadTick]);

  const selfId = state.status === 'authed' ? state.user.id : null;

  const tenantById = useMemo(() => {
    const m = new Map<string, TenantSummary>();
    for (const t of tenants) m.set(t.id, t);
    return m;
  }, [tenants]);

  return (
    <div className="space-y-6">
      <header className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-primary-container text-on-primary">
            <Icon name="manage_accounts" className="text-2xl" />
          </div>
          <div>
            <h1 className="font-headline-md text-headline-md text-on-surface">
              Users (all scopes)
            </h1>
            <p className="font-body-md text-body-md text-on-surface-variant">
              Every portal account across every tenant. Master admins only. For resident accounts
              use the{' '}
              <Link to="/residents" className="underline">
                Residents
              </Link>{' '}
              workflow.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setAction({ kind: 'invite', user: null })}
          className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-on-primary font-label-md text-label-md rounded-lg hover:bg-opacity-90 transition-all"
        >
          <Icon name="person_add" />
          <span>Invite user</span>
        </button>
      </header>

      {toast ? (
        <div
          role="status"
          className="bg-secondary-container text-on-secondary-container rounded-lg px-4 py-2 font-body-md text-body-md flex items-center gap-2"
        >
          <Icon name="check_circle" />
          <span className="flex-1">{toast}</span>
          <button type="button" onClick={() => setToast(null)} className="underline font-medium">
            Dismiss
          </button>
        </div>
      ) : null}

      <section className="bg-surface-container-lowest border border-outline-variant rounded-xl overflow-hidden">
        {error ? (
          <div className="p-6 text-error font-body-md text-body-md" role="alert">
            {error}
          </div>
        ) : rows == null ? (
          <div className="p-12 flex justify-center">
            <Spinner />
          </div>
        ) : rows.length === 0 ? (
          <div className="p-12 text-center text-on-surface-variant font-body-md text-body-md">
            No portal accounts yet.
          </div>
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-surface-container">
              <tr>
                <th className="text-left font-label-md text-label-md text-on-surface-variant uppercase px-4 py-3 w-[18%]">
                  Name
                </th>
                <th className="text-left font-label-md text-label-md text-on-surface-variant uppercase px-4 py-3">
                  Email
                </th>
                <th className="text-left font-label-md text-label-md text-on-surface-variant uppercase px-4 py-3 w-[14%]">
                  Role
                </th>
                <th className="text-left font-label-md text-label-md text-on-surface-variant uppercase px-4 py-3 w-[16%]">
                  Tenant
                </th>
                <th className="text-left font-label-md text-label-md text-on-surface-variant uppercase px-4 py-3 w-[10%]">
                  Status
                </th>
                <th className="text-left font-label-md text-label-md text-on-surface-variant uppercase px-4 py-3 w-[12%]">
                  Last login
                </th>
                <th className="text-right font-label-md text-label-md text-on-surface-variant uppercase px-4 py-3 w-[6%]">
                  &nbsp;
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-outline-variant">
              {rows.map((u) => {
                const t = u.tenant_id ? tenantById.get(u.tenant_id) : null;
                return (
                  <tr key={u.id} className="hover:bg-surface-container-low transition-colors">
                    <td className="px-4 py-3 font-body-md text-body-md text-on-surface">
                      <Link
                        to={`/admin/users/${u.id}`}
                        className="text-primary hover:underline"
                        data-testid={`admin-user-row-link-${u.id}`}
                      >
                        {u.name}
                      </Link>
                    </td>
                    <td className="px-4 py-3 font-body-md text-body-md text-on-surface">
                      {u.email}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center px-2 py-1 rounded-full font-label-md text-label-md ${ROLE_TONE[u.role]}`}
                      >
                        {roleLabel(u.role)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {u.tenant_id ? (
                        <span className="font-body-md text-body-md text-on-surface">
                          {t?.name ?? (
                            <span className="font-code text-code text-on-surface-variant">
                              {u.tenant_id.slice(0, 8)}…
                            </span>
                          )}
                        </span>
                      ) : (
                        <span className="text-on-surface-variant">global</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center px-2 py-1 rounded-full font-label-md text-label-md ${STATUS_TONE[u.status]}`}
                      >
                        {statusLabel(u.status)}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-body-md text-body-md text-on-surface-variant">
                      {u.last_login_at ? new Date(u.last_login_at).toLocaleDateString() : '—'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <ActionMenu
                        user={u}
                        isSelf={u.id === selfId}
                        open={openMenu === u.id}
                        onOpen={() => setOpenMenu(openMenu === u.id ? null : u.id)}
                        onAction={(kind) => {
                          setOpenMenu(null);
                          setAction({ kind, user: u });
                        }}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        )}
      </section>

      {action ? (
        <ActionModal
          state={action}
          tenants={tenants}
          onCancel={() => setAction(null)}
          onDone={(msg) => {
            setAction(null);
            setToast(msg);
            reload();
          }}
        />
      ) : null}
    </div>
  );
}

function ActionMenu({
  user,
  isSelf,
  open,
  onOpen,
  onAction,
}: {
  user: AdminUserRow;
  isSelf: boolean;
  open: boolean;
  onOpen: () => void;
  onAction: (kind: ActionKind) => void;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!open) return;
    function onClickAway(e: MouseEvent) {
      if (!ref.current?.contains(e.target as Node)) onOpen();
    }
    document.addEventListener('mousedown', onClickAway);
    window.addEventListener('resize', onOpen);
    window.addEventListener('scroll', onOpen);
    return () => {
      document.removeEventListener('mousedown', onClickAway);
      window.removeEventListener('resize', onOpen);
      window.removeEventListener('scroll', onOpen);
    };
  }, [open, onOpen]);

  return (
    <div className="relative inline-block" ref={ref}>
      <button
        type="button"
        onClick={onOpen}
        aria-haspopup="menu"
        aria-expanded={open}
        title="Actions"
        className="inline-flex items-center justify-center w-8 h-8 rounded-full hover:bg-surface-container-high"
      >
        <Icon name="more_vert" />
      </button>
      {open ? (
        <div
          role="menu"
          style={rowMenuStyle(ref.current)}
          className="bg-surface-container-lowest border border-outline-variant rounded-lg shadow-lg z-30 py-1"
        >
          <MenuItem label="Edit name" onClick={() => onAction('edit')} />
          <MenuItem
            label="Change role"
            onClick={() => onAction('role')}
            disabled={isSelf}
            disabledTitle="Use the auth surface to change your own account"
          />
          <MenuItem
            label="Move tenant"
            onClick={() => onAction('tenant')}
            disabled={isSelf}
            disabledTitle="Use the auth surface to change your own account"
          />
          {user.status === 'suspended' ? (
            <MenuItem label="Reactivate" onClick={() => onAction('reactivate')} disabled={isSelf} />
          ) : (
            <MenuItem
              label="Suspend"
              destructive
              onClick={() => onAction('suspend')}
              disabled={isSelf}
              disabledTitle="Cannot suspend yourself"
            />
          )}
          <MenuItem
            label="Delete permanently"
            destructive
            onClick={() => onAction('delete')}
            disabled={isSelf}
            disabledTitle="Cannot delete yourself"
          />
          <MenuItem
            label="Set password"
            onClick={() => onAction('reset-password')}
            disabled={isSelf}
          />
          <MenuItem label="Reset TOTP" onClick={() => onAction('reset-mfa')} disabled={isSelf} />
          <MenuItem label="Invalidate sessions" onClick={() => onAction('invalidate')} />
          {user.status === 'invited' ? (
            <MenuItem label="Resend invite" onClick={() => onAction('resend-invite')} />
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function MenuItem({
  label,
  onClick,
  disabled,
  disabledTitle,
  destructive,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  disabledTitle?: string;
  destructive?: boolean;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      disabled={disabled}
      onClick={onClick}
      title={disabled ? disabledTitle : undefined}
      className={`w-full text-left px-3 py-2 font-body-md text-body-md hover:bg-surface-container-low disabled:opacity-50 disabled:cursor-not-allowed ${
        destructive === true ? 'text-error' : 'text-on-surface'
      }`}
    >
      {label}
    </button>
  );
}

interface ActionModalProps {
  state: { kind: ActionKind; user: AdminUserRow | null };
  tenants: TenantSummary[];
  onCancel: () => void;
  onDone: (toast: string) => void;
}

function ActionModal({ state, tenants, onCancel, onDone }: ActionModalProps) {
  const { kind, user } = state;
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Per-action local state
  const [email, setEmail] = useState('');
  const [name, setName] = useState(user?.name ?? '');
  const [role, setRole] = useState<Role>(user?.role ?? 'tenant_viewer');
  const [tenantId, setTenantId] = useState<string | null>(user?.tenant_id ?? null);
  const [initialPassword, setInitialPassword] = useState('');
  const [resetPassword, setResetPassword] = useState('');
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [inviteResult, setInviteResult] = useState<{
    user: AdminUserRow;
    invite_token: string | null;
  } | null>(null);

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const id = user?.id;
      if (kind === 'delete' && deleteConfirmation !== 'DELETE') {
        setError('Type DELETE to confirm permanent removal.');
        setSubmitting(false);
        return;
      }
      switch (kind) {
        case 'invite': {
          const res = await apiClient.post<{
            data: { user: AdminUserRow; invite_token: string | null };
          }>(
            '/api/v1/admin/users',
            {
              email: email.trim().toLowerCase(),
              name: name.trim(),
              role,
              initial_password: initialPassword.trim() || undefined,
              ...(role === 'master_admin' ? {} : { tenant_id: tenantId }),
            },
            { skipTenantScope: true },
          );
          if (!res.data.invite_token) {
            onDone('User created with initial password.');
            return;
          }
          setInviteResult(res.data);
          return; // keep modal open to show invite_token
        }
        case 'edit':
          await apiClient.patch(
            `/api/v1/admin/users/${id}`,
            { name: name.trim() },
            { skipTenantScope: true },
          );
          onDone('Name updated.');
          return;
        case 'role':
          await apiClient.patch(
            `/api/v1/admin/users/${id}`,
            {
              role,
              ...(role === 'master_admin'
                ? { tenant_id: null }
                : tenantId
                  ? { tenant_id: tenantId }
                  : {}),
            },
            { skipTenantScope: true },
          );
          onDone('Role changed. Sessions for that user were revoked.');
          return;
        case 'tenant':
          await apiClient.patch(
            `/api/v1/admin/users/${id}`,
            { tenant_id: tenantId },
            { skipTenantScope: true },
          );
          onDone('Tenant moved. Sessions for that user were revoked.');
          return;
        case 'suspend':
          await apiClient.delete(`/api/v1/admin/users/${id}`, {
            skipTenantScope: true,
          });
          onDone('User suspended.');
          return;
        case 'delete':
          await apiClient.post(
            `/api/v1/admin/users/${id}/delete`,
            {},
            { skipTenantScope: true },
          );
          onDone('User permanently deleted.');
          return;
        case 'reactivate':
          await apiClient.patch(
            `/api/v1/admin/users/${id}`,
            { status: 'active' },
            { skipTenantScope: true },
          );
          onDone('User reactivated.');
          return;
        case 'reset-password':
          await apiClient.post(
            `/api/v1/admin/users/${id}/password/reset`,
            { new_password: resetPassword },
            { skipTenantScope: true },
          );
          onDone('Password set. Existing sessions were revoked.');
          return;
        case 'reset-mfa':
          await apiClient.post(
            `/api/v1/admin/users/${id}/mfa/reset`,
            {},
            { skipTenantScope: true },
          );
          onDone('TOTP reset. The user will be prompted to re-enroll.');
          return;
        case 'invalidate':
          await apiClient.post(
            `/api/v1/admin/users/${id}/sessions/invalidate`,
            {},
            { skipTenantScope: true },
          );
          onDone('Sessions invalidated.');
          return;
        case 'resend-invite': {
          // Resend by issuing a fresh invite via /admin/users POST is
          // wrong (would conflict on email). Backend exposes resend on
          // /users/:id/invite — fall back to PATCH that re-issues via
          // status=invited if the user is already invited. If neither
          // endpoint is present, surface the gap.
          try {
            await apiClient.post(
              `/api/v1/admin/users/${id}/invite/resend`,
              {},
              { skipTenantScope: true },
            );
          } catch (err) {
            if (err instanceof ApiError && err.status === 404) {
              throw new Error(
                'Resend-invite endpoint not available. Ask the operator to recreate the invite manually.',
                { cause: err },
              );
            }
            throw err;
          }
          onDone('Invite re-sent.');
          return;
        }
      }
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : (err as Error).message;
      // Last-master protection surface — backend uses HTTP 422 here.
      setError(msg);
      setSubmitting(false);
    }
  }

  // Render invite result step
  if (kind === 'invite' && inviteResult) {
    return (
      <ModalShell
        title="Invite created"
        icon="mark_email_unread"
        onCancel={() => onDone('Invite created.')}
        body={
          <div className="space-y-3">
            <p className="font-body-md text-body-md text-on-surface">
              The invite email was queued. If your environment doesn't deliver email, share the link
              below directly:
            </p>
            <pre className="font-code text-code bg-surface border border-outline-variant rounded p-2 overflow-x-auto">
              {`${window.location.origin}/accept-invite?token=${inviteResult.invite_token}`}
            </pre>
            <p className="font-body-md text-body-md text-on-surface-variant">
              This token is shown only once. It won't appear after you close this dialog.
            </p>
          </div>
        }
        primaryLabel="Done"
        primaryAction={() => onDone('Invite created.')}
      />
    );
  }

  const heading: Record<ActionKind, string> = {
    invite: 'Invite user',
    edit: `Edit ${user?.name}`,
    role: `Change role · ${user?.name}`,
    tenant: `Move tenant · ${user?.name}`,
    suspend: `Suspend ${user?.name}`,
    delete: `Delete ${user?.name} permanently`,
    reactivate: `Reactivate ${user?.name}`,
    'reset-password': `Set password · ${user?.name}`,
    'reset-mfa': `Reset TOTP · ${user?.name}`,
    invalidate: `Invalidate sessions · ${user?.name}`,
    'resend-invite': `Resend invite · ${user?.name}`,
  };

  const destructive =
    kind === 'suspend' ||
    kind === 'delete' ||
    kind === 'reset-password' ||
    kind === 'reset-mfa' ||
    kind === 'invalidate';
  const sessionWarn = kind === 'role' || kind === 'tenant';

  return (
    <ModalShell
      // eslint-disable-next-line security/detect-object-injection -- kind is a fixed union
      title={heading[kind]}
      icon={kind === 'invite' ? 'person_add' : 'edit'}
      destructive={destructive}
      onCancel={onCancel}
    >
      <form onSubmit={submit} className="space-y-4" noValidate>
        {kind === 'invite' ? (
          <>
            <Field label="Email" htmlFor="aux-email">
              <input
                id="aux-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={fieldCls}
              />
            </Field>
            <Field label="Name" htmlFor="aux-name">
              <input
                id="aux-name"
                type="text"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                className={fieldCls}
              />
            </Field>
            <Field label="Role" htmlFor="aux-role">
              <select
                id="aux-role"
                value={role}
                onChange={(e) => setRole(e.target.value as Role)}
                className={fieldCls}
              >
                {NON_RESIDENT_ROLES.map((r) => (
                  <option key={r} value={r}>
                    {roleLabel(r)}
                  </option>
                ))}
              </select>
            </Field>
            {role !== 'master_admin' ? (
              <Field label="Tenant" htmlFor="aux-tenant">
                <select
                  id="aux-tenant"
                  value={tenantId ?? ''}
                  onChange={(e) => setTenantId(e.target.value || null)}
                  className={fieldCls}
                >
                  <option value="">— select tenant —</option>
                  {tenants
                    .filter((t) => t.status === 'active')
                    .map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                </select>
              </Field>
            ) : null}
            <Field label="Initial password" htmlFor="aux-initial-password">
              <input
                id="aux-initial-password"
                type="password"
                minLength={12}
                value={initialPassword}
                onChange={(e) => setInitialPassword(e.target.value)}
                className={fieldCls}
                placeholder="Leave blank to send invite"
              />
            </Field>
          </>
        ) : kind === 'edit' ? (
          <Field label="Name" htmlFor="aux-name">
            <input
              id="aux-name"
              type="text"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={fieldCls}
            />
          </Field>
        ) : kind === 'role' ? (
          <>
            <Field label="Role" htmlFor="aux-role">
              <select
                id="aux-role"
                value={role}
                onChange={(e) => setRole(e.target.value as Role)}
                className={fieldCls}
              >
                {NON_RESIDENT_ROLES.map((r) => (
                  <option key={r} value={r}>
                    {roleLabel(r)}
                  </option>
                ))}
              </select>
            </Field>
            {role !== 'master_admin' ? (
              <Field label="Tenant" htmlFor="aux-tenant">
                <select
                  id="aux-tenant"
                  value={tenantId ?? ''}
                  onChange={(e) => setTenantId(e.target.value || null)}
                  className={fieldCls}
                >
                  <option value="">— keep current —</option>
                  {tenants
                    .filter((t) => t.status === 'active')
                    .map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                </select>
              </Field>
            ) : null}
          </>
        ) : kind === 'tenant' ? (
          <Field label="Tenant" htmlFor="aux-tenant">
            <select
              id="aux-tenant"
              value={tenantId ?? ''}
              onChange={(e) => setTenantId(e.target.value || null)}
              className={fieldCls}
            >
              <option value="">— global / no tenant —</option>
              {tenants
                .filter((t) => t.status === 'active')
                .map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
            </select>
          </Field>
        ) : kind === 'reset-password' ? (
          <Field label="New password" htmlFor="aux-reset-password">
            <input
              id="aux-reset-password"
              type="password"
              required
              minLength={8}
              value={resetPassword}
              onChange={(e) => setResetPassword(e.target.value)}
              className={fieldCls}
              placeholder="8+ characters"
            />
          </Field>
        ) : kind === 'delete' ? (
          <div className="space-y-3">
            <p className="font-body-md text-body-md text-error">
              This permanently removes the portal account. It revokes every session, reset token,
              and passkey, clears MFA, and anonymizes the account email. This cannot be undone.
            </p>
            <Field label='Type DELETE to confirm' htmlFor="aux-delete-confirmation">
              <input
                id="aux-delete-confirmation"
                type="text"
                required
                value={deleteConfirmation}
                onChange={(e) => setDeleteConfirmation(e.target.value)}
                className={fieldCls}
                autoComplete="off"
              />
            </Field>
          </div>
        ) : (
          <p className="font-body-md text-body-md text-on-surface">
            {kind === 'suspend'
              ? 'The user will be locked out and their sessions revoked. The action is audited.'
              : kind === 'reactivate'
                ? 'The user will be able to sign in again. They keep their old role and tenant.'
                : kind === 'reset-mfa'
                  ? 'Clears the TOTP enrollment and revokes existing sessions. The user must re-enroll on next sign-in.'
                  : kind === 'invalidate'
                    ? 'Drops every active session for this user. They will have to sign in again.'
                    : 'The user will receive a fresh invite email with a new token.'}
          </p>
        )}
        {sessionWarn ? (
          <div
            role="status"
            className="bg-tertiary-fixed text-on-tertiary-fixed rounded-lg px-3 py-2 font-body-md text-body-md"
          >
            <Icon name="warning" className="inline-block mr-1" />
            Sessions for this user will be revoked.
          </div>
        ) : null}
        {error ? (
          <p role="alert" className="text-error font-body-md text-body-md">
            {error}
          </p>
        ) : null}
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onCancel} disabled={submitting}>
            Cancel
          </Button>
          <Button type="submit" loading={submitting}>
            {kind === 'invite'
              ? 'Send invite'
              : kind === 'delete'
                ? 'Delete permanently'
                : destructive
                  ? 'Confirm'
                  : 'Save'}
          </Button>
        </div>
      </form>
    </ModalShell>
  );
}

const fieldCls =
  'w-full px-3 py-2 bg-surface border border-outline-variant rounded-lg font-body-md text-body-md focus:ring-2 focus:ring-primary outline-none';

function Field({
  label,
  htmlFor,
  children,
}: {
  label: string;
  htmlFor: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-2">
      <label htmlFor={htmlFor} className="font-label-md text-label-md text-on-surface block">
        {label}
      </label>
      {children}
    </div>
  );
}

function ModalShell({
  title,
  icon,
  destructive,
  onCancel: _onCancel,
  children,
  body,
  primaryLabel,
  primaryAction,
}: {
  title: string;
  icon: string;
  destructive?: boolean;
  onCancel: () => void;
  children?: React.ReactNode;
  body?: React.ReactNode;
  primaryLabel?: string;
  primaryAction?: () => void;
}) {
  return (
    <div
      role="dialog"
      aria-labelledby="user-action-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-on-surface/30 p-margin-mobile"
    >
      <div className="w-full max-w-[520px] max-h-[calc(100dvh-2rem)] overflow-y-auto bg-surface-container-lowest rounded-xl shadow-lg border border-outline-variant p-8">
        <header className="flex items-center gap-3 mb-6">
          <div
            className={`inline-flex items-center justify-center w-10 h-10 rounded-lg ${
              destructive
                ? 'bg-error-container text-on-error-container'
                : 'bg-primary-container text-on-primary'
            }`}
          >
            <Icon name={icon} />
          </div>
          <h2 id="user-action-title" className="font-headline-sm text-headline-sm text-on-surface">
            {title}
          </h2>
        </header>
        {body}
        {children}
        {primaryLabel && primaryAction ? (
          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" onClick={primaryAction}>
              {primaryLabel}
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
